import React, { useState } from 'react';
import { Accordion, Button, Checkbox, Form, Header, Icon, Input, List, Message, Segment } from 'semantic-ui-react';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';

const RoleItem = ({ role, onToggle }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { id, name, summary, permissions, locked, selected } = role;

    return (
        <Segment className={`Onboarding__recommendation ${selected ? 'Onboarding__recommendation--selected' : ''}`}>
            <div className='Onboarding__recommendation-header'>
                <Checkbox
                    id={`role-${id}`}
                    checked={selected}
                    disabled={locked}
                    onChange={() => onToggle(id)}
                    label={<label htmlFor={`role-${id}`}><b>{name}</b>{locked && <i> (always included)</i>}</label>}
                />
            </div>
            <p className='Onboarding__recommendation-summary'>{summary}</p>
            <Accordion>
                <Accordion.Title active={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
                    <Icon name='dropdown' /> View permissions
                </Accordion.Title>
                <Accordion.Content active={isExpanded}>
                    <List bulleted>
                        {permissions.map(permission => <List.Item key={permission}>{permission}</List.Item>)}
                    </List>
                    <p><i>You can fine-tune these later in Settings → Permissions.</i></p>
                </Accordion.Content>
            </Accordion>
        </Segment>
    );
}

const TeamStructure = ({
    teams,
    roles,
    showRoleSuggestions,
    teamErrors,
    teamLimit,
    canContinue,
    isStale,
    onRegenerate,
    onToggleTeam,
    onRenameTeam,
    onAddTeam,
    onRemoveTeam,
    onToggleRole,
    onBack,
    onContinue
}) => {
    const [newTeamName, setNewTeamName] = useState('');
    const selectedCount = teams.filter(({ selected }) => selected).length;
    const isAtTeamLimit = teamLimit !== null && teamLimit !== undefined && selectedCount >= teamLimit;
    const isOverTeamLimit = teamLimit !== null && teamLimit !== undefined && selectedCount > teamLimit;

    const addTeam = () => {
        if (newTeamName.trim() && !isAtTeamLimit) {
            onAddTeam(newTeamName.trim());
            setNewTeamName('');
        }
    }

    return (
        <Segment>
            <Header as='h3'>
                Suggested Team Structure
                <Header.Subheader>Based on your business profile. Rename, remove or add teams so they match how you work.</Header.Subheader>
            </Header>
            {isStale && (
                <Message warning size='small'>
                    Your business profile changed since these suggestions were made.
                    <Button size='tiny' basic compact style={{ marginLeft: '1em' }} onClick={onRegenerate}>Regenerate Suggestions</Button>
                </Message>
            )}

            {teamLimit === 0 ? (
                <Message info size='small'>
                    <Icon name='users' />
                    Your plan doesn't include teams, so everyone works as one group. Teams are available on plans that include them.
                </Message>
            ) : (<>
            <Header as='h4'>
                Teams <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
                <Header.Subheader>
                    Teams group users for lead assignment, call routing and reporting.
                    {teamLimit !== null && ` Your plan includes up to ${teamLimit} team${teamLimit === 1 ? '' : 's'} (${selectedCount} selected).`}
                </Header.Subheader>
            </Header>
            <Form>
                {teams.map(({ id, name, purpose, selected, is_custom }) => (
                    <Segment key={id} className={`Onboarding__recommendation ${selected ? 'Onboarding__recommendation--selected' : ''}`}>
                        <div className='Onboarding__team-row'>
                            <Checkbox checked={selected} disabled={!selected && isAtTeamLimit} onChange={() => onToggleTeam(id)} aria-label={`Include ${name}`} />
                            <Form.Field error={!!teamErrors[id]} className='Onboarding__team-name'>
                                <Input
                                    size='small'
                                    value={name}
                                    disabled={!selected}
                                    onChange={(_, { value }) => onRenameTeam(id, value)}
                                />
                                {teamErrors[id] && <div className='Onboarding__field-error'>{teamErrors[id]}</div>}
                            </Form.Field>
                            {is_custom && <Icon name='trash alternate outline' link color='red' onClick={() => onRemoveTeam(id)} />}
                        </div>
                        {purpose && <p className='Onboarding__recommendation-summary'>{purpose}</p>}
                    </Segment>
                ))}
                <Input
                    size='small'
                    fluid
                    placeholder='Add another team'
                    value={newTeamName}
                    onChange={(_, { value }) => setNewTeamName(value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTeam(); } }}
                    disabled={isAtTeamLimit}
                    action={{ icon: 'plus', content: 'Add Team', type: 'button', disabled: !newTeamName.trim() || isAtTeamLimit, onClick: addTeam }}
                />
            </Form>
            {isOverTeamLimit && (
                <Message warning size='small'>Your plan includes up to {teamLimit} team{teamLimit === 1 ? '' : 's'}. Turn off {selectedCount - teamLimit} to continue, or upgrade your plan for more.</Message>
            )}
            {isAtTeamLimit && !isOverTeamLimit && (
                <p className='Onboarding__hint'>You've reached your plan's team limit. Turn one off to pick a different team.</p>
            )}
            {!teams.some(({ selected }) => selected) && (
                <Message warning size='small'>Select at least one team to continue.</Message>
            )}
            </>)}

            {showRoleSuggestions ? (
                <>
                    <Header as='h4'>
                        Access Roles <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
                        <Header.Subheader>Roles control what each user can see and do.</Header.Subheader>
                    </Header>
                    {roles.map(role => <RoleItem key={role.id} role={role} onToggle={onToggleRole} />)}
                </>
            ) : (
                <Message info size='small' className='Onboarding__roles-note'>
                    <Icon name='key' />
                    Your plan uses DYL's standard access roles ({roles.map(({ name }) => name).join(', ')}). You'll pick a role for each person you invite.
                </Message>
            )}

            <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
            <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                Continue to Invites <Icon name='arrow right' />
            </Button>
            <div style={{ clear: 'both' }} />
        </Segment>
    );
}

export default TeamStructure;
